import RULES from './savedViewFilters.json';
import { AUTO_UPLOAD_FILTER_OPTIONS } from '../../../../components/chartActions/autoUploadFilterOptions';

// ENG-3551 saved views: the pure half of the feature. savedViewFilters.json is the
// single rules contract shared with SavedInboxView (Ruby); these helpers must agree
// with SavedInboxView.canonicalize_filters case for case — the shared conformance
// fixture (spec/fixtures/files/saved_view_canonicalization_cases.json) runs in both
// runtimes. Nothing here reads flags, storage or the network: effectful callers
// enforce the saved_views guard.

export const SAVED_VIEW_FILTER_RULES = RULES;
export const SAVED_VIEW_MARKER_KEY = 'saved_view';
export const SAVED_VIEW_PAGES = Object.keys(RULES.pages);
export const MAX_SAVED_VIEW_FILTER_BYTES = 8192;
export const MAX_SAVED_VIEW_NAME_LENGTH = 60;
export const DEFAULT_SAVED_VIEW_NAME = 'New view';
export const TEAM_SENTINEL_UNASSIGNED = 'unassigned';

export const SAVED_VIEW_COPY = {
  strip: 'Views',
  saveView: 'Save view',
  saveAsNew: 'Save as new',
  modified: 'Modified',
  updateView: 'Update view',
  revert: 'Revert',
  viewName: 'View name',
  excludedNote: 'Search, DOB and date ranges are not part of a view.',
  cancel: 'Cancel',
  save: 'Save',
  saving: 'Saving…',
  rename: 'Rename',
  renameTitle: 'Rename view',
  delete: 'Delete',
  retry: 'Retry',
  dismissNotice: 'Dismiss notice',
  unavailable: 'That saved view is no longer available.',
  lookupFailed: 'Couldn\'t check this view\'s filters.',
  listFailed: 'Couldn\'t load views.',
  saveFailed: 'Couldn\'t save the view.',
  deleteFailed: 'Couldn\'t delete the view.',
  accessDenied: 'You do not have access to this feature.',
  emptyTitle: 'No records match these filters',
  clearFilters: 'Clear filters',
  activeFiltersPrefix: 'Active filters: ',
  activeFiltersNone: 'None',
  nameRequired: 'Enter a view name.',
  nameTooLong: 'Use 60 characters or fewer.',
  nameUnsupported: 'Remove unsupported characters from the view name.',
  duplicateName: 'You already have a view with that name.',
  viewLimit: 'You can save up to 20 views on this page.',
  filtersNotObject: 'View filters must be a JSON object.',
  filtersTooLarge: 'View filters must be 8 KB or smaller.',
  invalidFilter: 'View filters contain an invalid value.',
  scopeConflict: 'Choose either a department or one record scope.',
  uploadConflict: 'Auto-Upload Not Attempted cannot be combined with other upload filters.',
  invalidPage: 'Choose inbox or referrals.',
  pageImmutable: 'A view\'s page cannot be changed.',
};

export const deleteConfirmCopy = (name) => `Delete “${name}”? Records are not affected.`;
export const viewOptionsLabel = (name) => `Options for ${name}`;

// Every 422 body SavedInboxView/SavedInboxViewsController can render. Anything else
// (or a 400) is shown as the generic save failure, never echoed raw.
export const SAVED_VIEW_VALIDATION_MESSAGES = [
  SAVED_VIEW_COPY.nameRequired,
  SAVED_VIEW_COPY.nameTooLong,
  SAVED_VIEW_COPY.nameUnsupported,
  SAVED_VIEW_COPY.duplicateName,
  SAVED_VIEW_COPY.viewLimit,
  SAVED_VIEW_COPY.filtersNotObject,
  SAVED_VIEW_COPY.filtersTooLarge,
  SAVED_VIEW_COPY.invalidFilter,
  SAVED_VIEW_COPY.scopeConflict,
  SAVED_VIEW_COPY.uploadConflict,
  SAVED_VIEW_COPY.invalidPage,
  SAVED_VIEW_COPY.pageImmutable,
];

// Astral (non-BMP) characters: the 3-byte utf8 DB connection cannot store them, so
// the server rejects them in names and filter values. Mirrors /[\u{10000}-\u{10FFFF}]/.
const ASTRAL_PATTERN = /[\u{10000}-\u{10FFFF}]/u;
export const containsAstral = (value) => ASTRAL_PATTERN.test(String(value));

export const codePointLength = (value) => Array.from(String(value)).length;

// Client copy of the server's name rule (trim, presence, 60 code points, BMP only).
// Returns the exact server copy, or null when the name is acceptable.
export const validateSavedViewName = (rawName) => {
  const name = String(rawName ?? '').trim();
  if (!name) return SAVED_VIEW_COPY.nameRequired;
  if (codePointLength(name) > MAX_SAVED_VIEW_NAME_LENGTH) return SAVED_VIEW_COPY.nameTooLong;
  if (containsAstral(name)) return SAVED_VIEW_COPY.nameUnsupported;
  return null;
};

// Unicode code-point order, matching Ruby's byte-wise UTF-8 String#<=>. The JS
// default sort compares UTF-16 code units, which orders U+F001 after an astral
// character's surrogate pair — the fixture pins that case.
export const compareCodePoints = (a, b) => {
  const left = String(a);
  const right = String(b);
  let i = 0;
  let j = 0;
  while (i < left.length && j < right.length) {
    const leftPoint = left.codePointAt(i);
    const rightPoint = right.codePointAt(j);
    if (leftPoint !== rightPoint) return leftPoint < rightPoint ? -1 : 1;
    i += leftPoint > 0xFFFF ? 2 : 1;
    j += rightPoint > 0xFFFF ? 2 : 1;
  }
  if (i < left.length) return 1;
  if (j < right.length) return -1;
  return 0;
};

// ActiveSupport's to_json writes <, > and & as 6-byte \uXXXX escapes, which
// JSON.stringify leaves as 1 byte; count them the same way so the 8 KB boundary
// agrees with Ruby. U+2028/9 stay raw under Rails 8.1 defaults
// (escape_js_separators_in_json = false), as they do in JSON.stringify.
const HTML_ESCAPED = /[<>&]/g;
export const jsonByteSize = (value) => {
  const json = JSON.stringify(value);
  const htmlCount = (json.match(HTML_ESCAPED) || []).length;
  return new TextEncoder().encode(json).length + (htmlCount * 5);
};

const isPlainObject = (value) => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const pageKeysFor = (page) => RULES.pages[page] || [];

const canonicalValue = (key, value) => {
  // Empty values are omitted for every key, presence-only ones included; only a
  // present non-true presence-only value is invalid.
  if (value === null || value === undefined || value === '') return [null, true];
  if (RULES.presenceOnlyKeys.includes(key)) {
    return ['true', value === true || value === 'true'];
  }

  if (RULES.listKeys.includes(key)) {
    const values = Array.isArray(value) ? value : [value];
    const valid = values.every((member) => typeof member === 'string' && !member.includes(','));
    if (!valid) return [null, false];
    if (values.length === 0) return [null, true];
    return [[...new Set(values)].sort(compareCodePoints), true];
  }
  if (Array.isArray(value) || typeof value === 'object') return [null, false];

  if (RULES.booleanKeys.includes(key)) {
    if (value === true || value === 'true') return ['true', true];
    return [null, value === false || value === 'false'];
  }
  if (RULES.stringKeys.includes(key)) return [value, typeof value === 'string'];

  const allowed = RULES.enums[key] || [];
  const valid = allowed.includes(value);
  return [RULES.defaults[key] === value ? null : value, valid];
};

const conflictError = (filters) => {
  const {
    recordScopeKeys, departmentKey, uploadKeys, exclusiveUploadKey,
  } = RULES.conflicts;
  const scopes = recordScopeKeys.filter((key) => key in filters).length;
  if (scopes > 1 || (scopes > 0 && departmentKey in filters)) return SAVED_VIEW_COPY.scopeConflict;

  const uploads = uploadKeys.filter((key) => key in filters).length;
  if (uploads > 1 && exclusiveUploadKey in filters) return SAVED_VIEW_COPY.uploadConflict;
  return null;
};

// SavedInboxView.canonicalize_filters, step for step. Never mutates its input.
export const canonicalizeFilters = (params, page) => {
  if (!isPlainObject(params)) return { filters: null, error: SAVED_VIEW_COPY.filtersNotObject };
  if (jsonByteSize(params) > MAX_SAVED_VIEW_FILTER_BYTES) {
    return { filters: null, error: SAVED_VIEW_COPY.filtersTooLarge };
  }
  if (!SAVED_VIEW_PAGES.includes(page)) return { filters: null, error: SAVED_VIEW_COPY.invalidPage };

  const pageKeys = pageKeysFor(page);
  const canonical = {};
  const keys = Object.keys(params);
  for (let index = 0; index < keys.length; index += 1) {
    const key = keys[index];
    if (pageKeys.includes(key)) {
      const [normalized, valid] = canonicalValue(key, params[key]);
      if (!valid) return { filters: null, error: SAVED_VIEW_COPY.invalidFilter };
      if (normalized !== null) canonical[key] = normalized;
    }
  }

  const filters = Object.keys(canonical).sort(compareCodePoints).reduce((acc, key) => {
    acc[key] = canonical[key];
    return acc;
  }, {});
  const error = conflictError(filters);
  if (error) return { filters: null, error };
  if (jsonByteSize(filters) > MAX_SAVED_VIEW_FILTER_BYTES) {
    return { filters: null, error: SAVED_VIEW_COPY.filtersTooLarge };
  }
  return { filters, error: null };
};

// Only the page's saveable keys, i.e. what a save would send. URL params also carry
// search/DOB/dates/page, which must not count toward the size limit or Modified.
export const pickSaveableParams = (params, page) => {
  if (!isPlainObject(params)) return params;
  const pageKeys = pageKeysFor(page);
  return Object.keys(params).reduce((acc, key) => {
    if (pageKeys.includes(key)) acc[key] = params[key];
    return acc;
  }, {});
};

// Canonical filters are key-sorted with sorted lists, so JSON identity is equality.
export const areFiltersEqual = (a, b, page) => {
  const left = canonicalizeFilters(pickSaveableParams(a, page), page);
  const right = canonicalizeFilters(pickSaveableParams(b, page), page);
  if (left.error || right.error) return false;
  return JSON.stringify(left.filters) === JSON.stringify(right.filters);
};

// Modified = the saveable state differs from the selected view's filters. Search,
// DOB, date bounds, page, page size, the marker and route invariants are not
// saveable, so they never make a view Modified.
export const isViewModified = (view, params, page) => {
  if (!view) return false;
  return !areFiltersEqual(view.filters || {}, params || {}, page);
};

// The update object Apply / Revert / flag-on Clear all hand to the bar's existing
// merge writer: every known filter key nulled (removing invisible legacy
// constraints too), then the view's canonical filters, then the marker and page 1.
export const buildSavedViewQueryUpdates = ({ page, view = null }) => {
  const updates = RULES.replacementKeys.reduce((acc, key) => {
    acc[key] = null;
    return acc;
  }, {});
  if (view) {
    Object.assign(updates, canonicalizeFilters(view.filters || {}, page).filters || {});
    updates[SAVED_VIEW_MARKER_KEY] = String(view.id);
  }
  updates.page = 1;
  if (page === 'referrals') {
    updates.referrals = 'true';
    updates.category = 'Referral';
  }
  return updates;
};

const isPresent = (value) => (
  value !== undefined && value !== null && value !== ''
  && !(Array.isArray(value) && value.length === 0)
);

// Filters that narrow rows but are never saved (search, DOB, date bounds and the
// legacy list keys). They count toward "No records match these filters".
const NARROWING_EXCLUDED_KEYS = RULES.excludedKeys.filter((key) => (
  !['page', 'page_size', SAVED_VIEW_MARKER_KEY, 'referrals'].includes(key)
));
const SORT_KEYS = ['sort', 'sort_dir'];

const isActiveSaveableValue = (key, value) => {
  if (!isPresent(value)) return false;
  if (RULES.booleanKeys.includes(key)) return value !== false && value !== 'false';
  return true;
};

// Any filter that narrows the list. Sort, the marker, pagination and the page's
// injected invariants (referrals' category) are not user filters.
export const hasActiveUserFilters = (params, page) => {
  const source = params || {};
  const pageExcluded = RULES.pageExcludedKeys[page] || [];
  const saveable = pageKeysFor(page).filter((key) => !SORT_KEYS.includes(key) && !pageExcluded.includes(key));
  return saveable.some((key) => isActiveSaveableValue(key, source[key]))
    || NARROWING_EXCLUDED_KEYS.some((key) => isPresent(source[key]));
};

const hasNondefaultSort = (source) => (
  isPresent(source.sort) || (isPresent(source.sort_dir) && source.sort_dir !== RULES.defaults.sort_dir)
);

// Flag-on Clear all visibility: anything the null-list would reset, including a
// nondefault sort or a selected view.
export const canClearSavedViewState = (params, page) => {
  const source = params || {};
  if (hasActiveUserFilters(source, page)) return true;
  if (isPresent(source[SAVED_VIEW_MARKER_KEY])) return true;
  return hasNondefaultSort(source);
};

// Reference keys whose values must still exist before a view may be applied.
export const REFERENCE_KEYS = {
  inbox: ['team', 'department', 'tag', 'category'],
  referrals: ['team', 'department', 'tag', 'referral_statuses', 'assignee'],
};

const asList = (value) => {
  if (!isPresent(value)) return [];
  return (Array.isArray(value) ? value : [value]).map(String);
};

const valuesMatch = (key, left, right) => (
  key === 'assignee'
    ? String(left).toLowerCase() === String(right).toLowerCase()
    : String(left) === String(right)
);

// Reference values a view needs, per key, in contract order. The team sentinel
// needs no lookup.
export const referencedValues = (filters, page) => (REFERENCE_KEYS[page] || []).reduce((acc, key) => {
  const values = asList((filters || {})[key]).filter((value) => (
    !(key === 'team' && value === TEAM_SENTINEL_UNASSIGNED)
  ));
  if (values.length > 0) acc[key] = values;
  return acc;
}, {});

// options[key] = { status: 'ready'|'loading'|'failed', values: [{ value, label }],
// knownLabels?: { [value]: label } }. Missing values are reported, never dropped.
export const findMissingReferences = (filters, page, options = {}) => {
  const missing = [];
  const unverifiable = [];
  const needed = referencedValues(filters, page);
  Object.keys(needed).forEach((key) => {
    const option = options[key];
    if (!option || option.status !== 'ready' || !Array.isArray(option.values)) {
      unverifiable.push(key);
      return;
    }
    [...needed[key]].sort(compareCodePoints).forEach((value) => {
      const found = option.values.some((candidate) => valuesMatch(key, candidate.value, value));
      if (!found) {
        missing.push({ key, value, label: option.knownLabels?.[value] || value });
      }
    });
  });
  return { missing, unverifiable };
};

const MISSING_REFERENCE_NOUNS = {
  tag: 'Label',
  team: 'Team',
  department: 'Department',
  category: 'Category',
  referral_statuses: 'Status',
  assignee: 'Assignee',
};

export const missingReferencesCopy = (missing) => {
  const items = missing.map(({ key, label }) => `${MISSING_REFERENCE_NOUNS[key]} “${label}”`).join(', ');
  return `This view uses filters that no longer exist: ${items}. Your current filters have not changed.`;
};

const SORT_PRESET_LABELS = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  dob_youngest: 'DOB (youngest)',
  dob_oldest: 'DOB (oldest)',
};

export const sortPresetLabel = (params) => {
  const source = params || {};
  if (source.sort === 'dob' && source.sort_dir === 'asc') return SORT_PRESET_LABELS.dob_oldest;
  if (source.sort === 'dob') return SORT_PRESET_LABELS.dob_youngest;
  if (source.sort_dir === 'asc') return SORT_PRESET_LABELS.oldest;
  return SORT_PRESET_LABELS.newest;
};

const UPLOAD_KEYS = RULES.conflicts.uploadKeys;
const UPLOAD_LABELS = AUTO_UPLOAD_FILTER_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

// Control names as the existing filter bars word them. Boolean controls render as
// their own label; list/string controls as `Name: value, value`.
export const SUMMARY_LABELS = {
  archived: { true: 'Archived', both: 'All Records' },
  team: 'Team',
  department: 'Department',
  tag: 'Label',
  unread: 'Unread',
  unassigned: 'Unassigned',
  autoUpload: 'Auto Upload Status',
  category: 'Category',
  myDepartmentOnly: 'My department\'s records',
  myRecordsOnly: 'My records',
  myTaggedRecordsOnly: 'Records I\'m tagged in',
  formSubmissions: 'Form submissions',
  emailSubmissions: 'Email submissions',
  includeReferrals: 'Include Referrals',
  non_referrals: 'Non-referrals',
  referral_statuses: 'Status',
  scheduled: 'Scheduled',
  overdue: 'Overdue',
  assignee: 'Assignee',
  reply_statuses: 'Reply Status',
  sms_statuses: 'SMS Status',
};

const optionLabel = (key, value, options) => {
  if (key === 'team' && value === TEAM_SENTINEL_UNASSIGNED) return 'No teams';
  const option = options?.[key];
  const match = (option?.values || []).find((candidate) => valuesMatch(key, candidate.value, value));
  return match?.label || option?.knownLabels?.[value] || value;
};

// One plain-text line describing the active saveable filters, in contract key
// order, so constraints whose controls are hidden for this user stay visible.
// Rendered as React text; never sent to analytics.
export const formatSavedViewSummary = (params, page, options = {}) => {
  const source = params || {};
  const pageExcluded = RULES.pageExcludedKeys[page] || [];
  const parts = [];
  let sortDone = false;
  let uploadDone = false;

  pageKeysFor(page).forEach((key) => {
    if (pageExcluded.includes(key)) return;
    if (SORT_KEYS.includes(key)) {
      if (sortDone) return;
      sortDone = true;
      if (hasNondefaultSort(source)) parts.push(sortPresetLabel(source));
      return;
    }
    if (UPLOAD_KEYS.includes(key)) {
      if (uploadDone) return;
      uploadDone = true;
      const active = UPLOAD_KEYS.filter((uploadKey) => isActiveSaveableValue(uploadKey, source[uploadKey]));
      if (active.length > 0) {
        parts.push(`${SUMMARY_LABELS.autoUpload}: ${active.map((uploadKey) => UPLOAD_LABELS[uploadKey] || uploadKey).join(', ')}`);
      }
      return;
    }
    const value = source[key];
    if (!isActiveSaveableValue(key, value)) return;
    if (key === 'archived') {
      parts.push(SUMMARY_LABELS.archived[String(value)] || String(value));
      return;
    }
    if (RULES.booleanKeys.includes(key) || RULES.presenceOnlyKeys.includes(key)) {
      parts.push(SUMMARY_LABELS[key] || key);
      return;
    }
    const labels = asList(value).map((item) => optionLabel(key, item, options));
    parts.push(`${SUMMARY_LABELS[key] || key}: ${labels.join(', ')}`);
  });

  return parts.length > 0 ? parts.join(' · ') : SAVED_VIEW_COPY.activeFiltersNone;
};
